import React, { useState, useEffect, useRef } from 'react';
import './InputTable.scss';
import InputSelect from '../inputSelect/InputSelect';
import ErrorModal from '../ErrorModal/ErrorModal';
import { getProductBatchList, getProductList } from '../../services/products';
import { toast } from 'react-toastify';
import { getDate } from '../../utils/dateUtils';
import InputField from '../form/FormInput';
import { useSelector } from 'react-redux';
import AddProductBatchModel from '../../pages/v2/sales/invoice/components/addProductBatchModel';

const PurchaseBillsTable = ({
	pbId,
	initialRows,
	columns,
	className,
	onChange,
	value,
	isDraft,
	disabled,
	isResetTable,
	newRowgenerate = '',
	setErrorCells,
	errorCells,
	tableError,
	setIsTableErrorShow,
	purchaseOrderId,
}) => {
	// enable_quantity_edit_on_purchase_bill
	const user = useSelector((state) => state?.userInfo?.userInfo?.data);
	const [batchOptions, setBatchOptions] = useState([]);
	// Track batch IDs created in current session to visually highlight them in dropdown
	const [newlyCreatedBatchIds, setNewlyCreatedBatchIds] = useState([]);
	const [productOptions, setProductOptions] = useState([]);
	const [selectedRowData, setSelectedRowData] = useState({});
	const [rows, setRows] = useState([]);
	const [showError, setShowError] = useState(false);
	const [isOpenProductBatchModel, setIsOpenProductBatchModel] = useState(false);
	const [tableIndex, setTableIndex] = useState(null);
	const inputRefs = useRef([]);
	const [loading, setLoading] = useState(false);

	const getQuantityDisabled = () => {
		if (!purchaseOrderId) {
			return false;
		}

		return user?.settings?.general_preferences?.enable_quantity_edit_on_purchase_bill &&
			user.settings?.general_preferences?.enable_quantity_edit_on_purchase_bill?.value
			? false
			: true;
	};

	async function fetchProduct(param) {
		try {
			const productList = await getProductList(param);
			const productOptions = productList?.productWithStockCount?.map((ele) => ({
				key: ele?.product_id,
				value: ele?.product_name,
			}));
			setProductOptions(productOptions);
		} catch (error) {
			toast.error('Something went wrong! while fetching the product list.');
		}
	}

	useEffect(() => {
		let param = {
			page: 1,
			pageSize: 2000,
			filterBy: 'status',
			filterValue: '1',
		};
		fetchProduct(param);
	}, []);

	useEffect(() => {
		if (errorCells.length > 0) {
			setIsTableErrorShow(true);
		} else {
			setIsTableErrorShow(false);
		}
	}, [errorCells]);

	useEffect(() => {
		if (tableError && rows?.length > 0 && errorCells.length > 0) {
			const secondLastRowIndex = rows.length - 1;
			const secondLastRow = rows[secondLastRowIndex];
			const errors = columns
				.filter((col) => {
					const value = secondLastRow[col.name];
					return (
						(value === null ||
							value === '' ||
							value === '0.00' ||
							value === 'MM/YY' ||
							Number(value) === 0) &&
						['product_id', 'batch_no', 'quantity', 'purchase_rate', 'mrp'].includes(col.name)
					);
				})
				.map((col) => ({
					index: secondLastRowIndex,
					name: col.name,
				}));
			setErrorCells((prevErrorCells) => [
				...prevErrorCells.filter(
					(er) =>
						!(
							er.index === secondLastRowIndex &&
							(er.customErrorMessage === null || er.customErrorMessage === undefined)
						),
				),
				...prevErrorCells.filter(
					(errs) =>
						errs.index === secondLastRowIndex &&
						(errs.name === 'mrp' || errs.name === 'purchase_rate'),
				),
				...errors,
			]);
		}
	}, [tableError]);

	useEffect(() => {
		function changeRow() {
			if (pbId) {
				setRows(isResetTable ? initialRows : value.length > 0 ? value : []);
			} else {
				setRows(isResetTable ? initialRows : value.length > 0 ? value : initialRows);
			}
		}
		changeRow();
	}, [value, initialRows]);

	const addRow = () => {
		const emptyRow = columns.reduce((acc, col) => {
			acc[col.name] = '';
			return acc;
		}, {});
		const newRows = [...rows, emptyRow];
		setRows(newRows);
		if (newRows.length > 1) {
			const secondLastRowIndex = newRows.length - 2;
			const secondLastRow = newRows[secondLastRowIndex];
			const errors = columns
				.filter((col) => {
					const value = secondLastRow[col.name];
					return (
						(value === null ||
							value === '' ||
							value === '0.00' ||
							value === 'MM/YY' ||
							Number(value) === 0) &&
						['product_id', 'batch_no', 'quantity', 'purchase_rate', 'mrp'].includes(col.name)
					);
				})
				.map((col) => ({
					index: secondLastRowIndex,
					name: col.name,
				}));

			setErrorCells((prevErrorCells) => [
				...prevErrorCells.filter(
					(er) =>
						!(
							er.index === secondLastRowIndex &&
							(er.customErrorMessage === null || er.customErrorMessage === undefined)
						),
				),
				...prevErrorCells.filter(
					(errs) =>
						errs.index === secondLastRowIndex &&
						(errs.name === 'mrp' || errs.name === 'purchase_rate'),
				),
				...errors,
			]);
		}

		if (onChange) {
			onChange(newRows);
		}
		setTimeout(() => {
			inputRefs.current[newRows.length - 1]?.[0]?.focus();
			let lastRowRef = inputRefs.current[newRows.length - 1]?.[0];
			lastRowRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}, 0);
	};

	const deleteRow = (index) => {
		if (rows.length > 1) {
			const newRows = [...rows];
			newRows.splice(index, 1);
			let newErrorCells = errorCells
				.filter((error) => error.index !== index)
				.map((error) => ({
					...error,
					index: error.index > index ? error.index - 1 : error.index,
				}));
			let productCounts = {};
			newRows.forEach((row, i) => {
				if (row.product_id) {
					productCounts[row.product_id] = (productCounts[row.product_id] || 0) + 1;
				}
			});
			newErrorCells = newErrorCells.filter(
				(error) =>
					error.name !== 'product_id' || productCounts[newRows[error.index]?.product_id] > 1,
			);

			newErrorCells = newErrorCells.filter((error) => error.name !== 'batch_no');

			// Step 3: Recalculate duplicate batch errors
			const batchCount = {};
			newRows.forEach((row) => {
				if (row.batch_no) {
					batchCount[row.batch_no] = (batchCount[row.batch_no] || 0) + 1;
				}
			});

			newRows.forEach((row, i) => {
				if (row.batch_no && batchCount[row.batch_no] > 1) {
					newErrorCells.push({
						index: i,
						name: 'batch_no',
						customErrorMessage: {
							batch_no: 'This batch number already exists in the purchase bill.',
						},
					});
				}
			});

			setErrorCells(newErrorCells);
			setRows(newRows);

			if (onChange) {
				onChange(newRows);
			}

			setTimeout(() => {
				inputRefs.current[newRows.length - 1]?.[0]?.focus();
			}, 0);
		} else if (!pbId) {
			setRows(initialRows);
			if (onChange) {
				onChange(initialRows);
			}
			setTimeout(() => {
				inputRefs.current[0]?.[0]?.focus();
			}, 0);
		}
	};

	const handleProductInputChange = async (index, value) => {
		setBatchOptions([]);
		const newRows = [...rows];
		const selectedProduct = productOptions?.find((prod) => prod?.key === value);
		newRows[index].product_id = selectedProduct?.key || '';
		var data;
		if (!isNaN(value) && value !== '') {
			data = await getProductList({
				filterBy: 'product_id',
				filterValue: value,
			});
		} else {
			data = null;
		}

		setSelectedRowData((prevData) => ({
			...prevData,
			[index]: [],
		}));
		if (data != null && data?.productWithStockCount?.length > 0) {
			newRows[index].product_tax1 = data?.productWithStockCount[0]?.tax1 ?? 0;
			newRows[index].product_tax2 = data?.productWithStockCount[0]?.tax2 ?? 0;
			newRows[index].sell_loose = data?.productWithStockCount[0]?.sell_loose ?? false;
			newRows[index].contains = Number(data?.productWithStockCount[0]?.contains) || 1;

			const batch = data?.productWithStockCount[0]?.product_batch?.map((ele) => ({
				key: ele.batch_no,
				value: ele.batch_no,
				product_batch_id: ele.product_batch_id,
				isNewlyCreated: newlyCreatedBatchIds.includes(ele.product_batch_id),
			}));

			setBatchOptions(batch || []);
		} else {
			newRows[index].product_tax1 = 0;
			newRows[index].product_tax2 = 0;
			setBatchOptions([]);
		}

		let error = [...errorCells.filter((er) => er.name !== 'product_id')];
		const productCounts = {};
		newRows.forEach((row, i) => {
			if (!row.product_id) {
				error.push({ index: i, name: 'product_id' });
			}
			if (row.product_id) {
				productCounts[row.product_id] = (productCounts[row.product_id] || 0) + 1;
			}
		});

		// newRows.forEach((row, i) => {
		// 	if (row.product_id && productCounts[row.product_id] > 1) {
		// 		error.push({
		// 			index: i,
		// 			name: 'product_id',
		// 			customErrorMessage: { product_id: 'This product already exists in the Purchase order.' },
		// 		});
		// 	}
		// });
		newRows[index].product_id = value;
		newRows[index].batch_no = '';
		newRows[index].mfg_date = '';
		newRows[index].exp_date = '';
		newRows[index].mrp = '';
		newRows[index].purchase_rate = '';
		newRows[index].quantity = '';
		newRows[index].free_quantity = '0';
		newRows[index].amount = '';
		setRows(newRows);
		setErrorCells(error);
		if (onChange) {
			onChange(newRows);
		}
	};

	const handleBatchChange = async (index, value, name) => {
		const newRows = [...rows];
		if (
			batchOptions &&
			newRows[index].product_id !== '' &&
			newRows[index].product_id !== undefined
		) {
			const data = await getProductList({
				filterBy: 'product_id',
				filterValue: newRows[index].product_id,
			});
			let batchData = data?.productWithStockCount[0]?.product_batch?.filter(
				(row) => row.batch_no === value,
			);
			if (batchData) {
				newRows[index].purchase_rate = batchData[0]?.purchase_rate ?? '0';
				newRows[index].mfg_date =
					batchData[0]?.mfg_date !== '' &&
					batchData[0]?.mfg_date !== null &&
					batchData[0]?.mfg_date !== undefined
						? getDate(batchData[0]?.mfg_date, false, false, true)
						: '';
				newRows[index].exp_date =
					batchData[0]?.expiry_date !== '' &&
					batchData[0]?.expiry_date !== null &&
					batchData[0]?.expiry_date !== undefined
						? getDate(batchData[0]?.expiry_date, false, false, true)
						: '';
				newRows[index].mrp = batchData[0]?.mrp ?? '';
			} else {
				newRows[index].purchase_rate = '';
				newRows[index].mfg_date = '';
				newRows[index].exp_date = '';
				newRows[index].mrp = '';
			}
		} else {
			newRows[index].purchase_rate = '';
			newRows[index].mfg_date = '';
			newRows[index].exp_date = '';
			newRows[index].mrp = '';
		}

		newRows[index][name] = value;
		let totalAmountWithDiscount =
			Number(newRows[index].purchase_rate ?? 0) * Number(newRows[index].quantity ?? 0) -
			(Number(newRows[index].purchase_rate ?? 0) *
				Number(newRows[index].quantity ?? 0) *
				Number(newRows[index].discount ?? 0)) /
				100;
		newRows[index].amount = totalAmountWithDiscount;
		setRows(newRows);

		if (onChange) {
			onChange(newRows);
		}
		// if (value) {
		// 	let updatedErrorCell = errorCells.filter(
		// 		(cell) => !(cell.index === index && cell.name === 'batch_no'),
		// 	);
		// 	setErrorCells(updatedErrorCell);
		// } else {
		// 	setErrorCells([...errorCells, { index, name: 'batch_no' }]);
		// }

		let error = [...errorCells.filter((er) => er.name !== 'batch_no')];
		const batchCount = {};
		newRows.forEach((row, i) => {
			if (!row.batch_no) {
				error.push({ index: i, name: 'batch_no' });
			}

			if (row.batch_no) {
				batchCount[row.batch_no] = (batchCount[row.batch_no] || 0) + 1;
			}
		});

		newRows.forEach((row, i) => {
			if (row.batch_no && batchCount[row.batch_no] > 1) {
				error.push({
					index: i,
					name: 'batch_no',
					customErrorMessage: {
						batch_no: 'This batch number already exists in the purchase bill.',
					},
				});
			}
		});
		setErrorCells(error);
		checkErrorForUpdateBatchNumber(index);
	};

	const handleDateChange = (index, value, field) => {
		value = value.replace(/\D/g, '');

		if (value.length > 4) {
			value = value.slice(0, 4);
		}

		if (value.length >= 2) {
			let month = value.slice(0, 2);
			const year = value.slice(2);
			if (parseInt(month) > 12) {
				month = '12';
			}

			value = year ? `${month}/${year}` : month;
		}

		const newRows = [...rows];
		newRows[index][field] = value;
		setRows(newRows);
		if (onChange) {
			onChange(newRows);
		}

		const currentDate = new Date();
		const ThisYear = currentDate.getFullYear() % 100;
		const ThisMonth = currentDate.getMonth() + 1;
		const currentRow = newRows[index];
		const mfgDate = currentRow?.mfg_date;
		const expDate = currentRow?.exp_date;
		const [mfgMonth, mfgYear] = mfgDate ? mfgDate.split('/').map(Number) : [null, null];
		const [expMonth, expYear] = expDate ? expDate.split('/').map(Number) : [null, null];

		let errors = [...errorCells];

		let isMfgDateInvalid = false;
		if (field === 'mfg_date') {
			if (!mfgDate) {
				errors = errors.filter((cell) => !(cell.index === index && cell.name === 'mfg_date'));
			} else if (mfgYear > ThisYear || (mfgYear === ThisYear && mfgMonth > ThisMonth)) {
				isMfgDateInvalid = true;
			} else {
				errors = errors.filter((cell) => !(cell.index === index && cell.name === 'mfg_date'));
			}
		}

		let isExpDateInvalid = false;
		if (field === 'exp_date' || field === 'mfg_date') {
			if (!expDate) {
				errors = errors.filter((cell) => !(cell.index === index && cell.name === 'exp_date'));
			} else if (
				(mfgYear && expYear < mfgYear) ||
				(mfgYear && mfgYear === expYear && expMonth < mfgMonth)
			) {
				isExpDateInvalid = true;
			} else {
				errors = errors.filter((cell) => !(cell.index === index && cell.name === 'exp_date'));
			}
		}

		if (isMfgDateInvalid) {
			errors.push({ index, name: 'mfg_date' });
		}
		if (isExpDateInvalid) {
			errors.push({ index, name: 'exp_date' });
		}

		setErrorCells(errors);
	};

	const handleChangeDefault = (index, valueData, field) => {
		let currentRowValue = rows[index];
		let value = '';

		if (field === 'quantity' || field === 'free_quantity') {
			value = valueData?.replace(/[^0-9]/g, '');
		} else {
			value = valueData?.replace(/[^0-9.]/g, '');
			const decimalCount = (value.match(/\./g) || []).length;
			if (decimalCount > 1) {
				value = +value?.slice(0, value.lastIndexOf('.'));
			}
		}

		let errors = [...errorCells];

		const newRows = [...rows];
		newRows[index][field] = field === 'discount' && value === '' ? '' : value;

		let updatedMrp = Number(newRows[index].mrp ?? 0);
		let updatedPurchaseRate = Number(newRows[index].purchase_rate ?? 0);

		if (field === 'mrp') {
			updatedMrp = Number(value);
			newRows[index].mrp = value;
		}

		if (field === 'purchase_rate') {
			updatedPurchaseRate = Number(value);
			newRows[index].purchase_rate = value;
		}

		if (updatedMrp < updatedPurchaseRate || updatedMrp === 0 || updatedPurchaseRate < 0) {
			errors = errors.filter(
				(cell) => !(cell.index === index && (cell.name === 'mrp' || cell.name === 'purchase_rate')),
			);
			if (updatedMrp < updatedPurchaseRate) {
				errors.push({ index, name: 'mrp' });
				errors.push({ index, name: 'purchase_rate' });
			}
			if (updatedMrp === 0) errors.push({ index, name: 'mrp' });
			if (updatedPurchaseRate < 0) errors.push({ index, name: 'purchase_rate' });
		} else {
			errors = errors.filter(
				(cell) => !(cell.index === index && (cell.name === 'mrp' || cell.name === 'purchase_rate')),
			);
		}

		if (field === 'free_quantity') {
			if (value === '' || Number(value) === 0) {
				if (Number(currentRowValue?.quantity) === 0 && Number(value) === 0) {
					errors.push({ index, name: field });
				}
			} else {
				errors = errors.filter((cell) => !(cell.index === index && cell.name === field));
			}
		}

		if (field === 'quantity') {
			if (value === '' || (Number(currentRowValue?.free_quantity) === 0 && Number(value) === 0)) {
				errors.push({ index, name: field });
			} else {
				errors = errors.filter((cell) => !(cell.index === index && cell.name === field));
			}
		}

		setErrorCells(errors);
		let totalAmountWithDiscount =
			updatedPurchaseRate * Number(newRows[index].quantity ?? 0) -
			(updatedPurchaseRate *
				Number(newRows[index].quantity ?? 0) *
				Number(newRows[index].discount ?? 0)) /
				100;

		newRows[index].amount = totalAmountWithDiscount;
		setRows(newRows);

		if (onChange) {
			onChange(newRows);
		}
	};

	const handleKeyDown = (index, event) => {
		let { name, value } = event.target;
		if (event.key === 'Enter') {
			const column = columns.find((col) => col.name === name);
			if ((column && column.label === newRowgenerate) || (column && column.label === 'Amount')) {
				addRow();
			}
		}
	};

	const handleRef = (rowIndex, cellIndex) => (el) => {
		if (!inputRefs.current[rowIndex]) {
			inputRefs.current[rowIndex] = [];
		}

		inputRefs.current[rowIndex][cellIndex] = el;
	};

	const getToolTipData = (name) => {
		switch (name) {
			case 'product_id':
				return 'Product Name can not be empty.';
			case 'batch_no':
				return 'Batch No can not be empty';
			case 'quantity':
				return 'Quantity can not be empty or 0';
			case 'free_quantity':
				return 'free quantity can not be empty';
			case 'mrp':
				return 'MRP can not be empty';
			case 'purchase_rate':
				return 'Purchase rate can not be empty';
			case 'amount':
				return `Amount can not empty`;
			default:
				return '';
		}
	};

	useEffect(() => {
		if (rows.length > 0 && onChange) {
			onChange(rows);
		}
	}, [disabled]);

	const ErrorModalOpen = () => {
		setShowError(true);
	};

	const CloseErrorModal = () => {
		setShowError(false);
		setTimeout(() => {
			if (rows.length > 0) {
				inputRefs.current[rows.length - 1]?.[0]?.focus();
			}
		}, 0);
	};

	const numberOfProducts = rows.length;

	async function handleFetchBatchNumber(rowIndex, colName, productId) {
		try {
			setBatchOptions([]);
			if (productId !== undefined && productId !== '' && productId !== null) {
				const data = await getProductBatchList({ product_id: productId, type: 'pb_dropdown' });
				const productBatches =
					data?.length > 0
						? data?.map((batch) => {
								return {
									key: batch?.batch_no,
									value: batch?.batch_no,
									product_batch_id: batch?.product_batch_id,
									isNewlyCreated: newlyCreatedBatchIds.includes(batch?.product_batch_id),
								};
							})
						: [];
				setBatchOptions(productBatches);
			}
		} catch (error) {
			setBatchOptions([]);
			toast.error('Something went wrong!');
		}
	}

	function checkErrorForUpdateBatchNumber(rowIndex) {
		const row = rows[rowIndex];
		const errors = columns
			.filter((col) => {
				const value = row[col.name];

				return (
					(value === null ||
						value === '' ||
						value === 'MM/YY' ||
						value === '0.00' ||
						value === '0' ||
						Number(value) === 0) &&
					['product_id', 'batch_no', 'quantity', 'mrp'].includes(col.name)
				);
			})
			.map((col) => ({
				index: rowIndex,
				name: col.name,
			}));

		setErrorCells((prevError) => [
			...prevError.filter(
				(er) =>
					!(
						er.index === rowIndex &&
						(er.customErrorMessage === null || er.customErrorMessage === undefined)
					),
			),
			...errors,
		]);

		// setErrorCells((prevErrorCells) => [
		// 	...prevErrorCells.filter((error) => !(error.index === rowIndex)),
		// 	...errors,
		// ]);
	}

	return (
		<>
			{errorCells && errorCells?.length > 0 && (
				<div className="d-flex justify-content-end  text-danger  mb-0">
					{' '}
					<span
						style={{ textDecoration: 'underline', cursor: 'pointer' }}
						onClick={ErrorModalOpen}
					>
						Show all error
					</span>
				</div>
			)}
			<div className="text-center">
				<div className=" custom-scroll">
					<table className="purchase-table">
						<thead>
							<tr>
								{columns.map((col) => (
									<th
										className={`${className}`}
										key={col.name}
									>
										<div
											style={{
												display: 'flex',
												justifyItems: 'start',
												alignItems: 'center',
											}}
										>
											<span>{col.label}</span>
											<span style={{ color: 'red' }}>
												{[
													'product_id',
													'batch_no',
													'quantity',
													'free_quantity',
													'mrp',
													'amount',
												].includes(col.name)
													? '*'
													: ''}
											</span>
											{[
												'product_id',
												'batch_no',
												'quantity',
												'free_quantity',
												'purchase_rate',
												'mfg_date',
												'exp_date',
												'mrp',
												'amount',
											].includes(col.name) ? (
												<i className="toolTip_custum fa fa-info-circle">
													<span className="toolTip_custumtext">{getToolTipData(col.name)}</span>
												</i>
											) : (
												''
											)}
										</div>
									</th>
								))}
								<th>Actions</th>
							</tr>
						</thead>
						{loading ? (
							<div
								className="d-flex justify-content-center align-items-center"
								style={{
									marginTop: '5%',
									marginLeft: '40%',
									position: 'fixed ',
								}}
							>
								<span
									className="Page_loader"
									role="status"
									aria-hidden="true"
								></span>
							</div>
						) : (
							<tbody>
								{rows.map((row, rowIndex) => (
									<tr key={rowIndex}>
										{columns.map((col, cellIndex) => (
											<td
												key={col.name}
												className={
													errorCells.some(
														(cell) => cell.index === rowIndex && cell.name === col.name,
													)
														? 'error'
														: ''
												}
											>
												{col.type === 'batch_no' ? (
													<InputSelect
														placeholder="Select Batch No"
														value={
															batchOptions?.find((option) => option.key === row[col.name])?.value ||
															row[col.name]
														}
														isNewlyCreated={
															Boolean(row[col.name]) &&
															newlyCreatedBatchIds.some(
																(id) =>
																	(row?.product_batch_id &&
																		String(id) === String(row?.product_batch_id)) ||
																	(row[col.name] && String(id) === String(row[col.name])),
															)
														}
														onChange={(value) => {
															if (value === '') {
																const newRows = [...rows];
																newRows[rowIndex].purchase_rate = '0.00';
																newRows[rowIndex].mfg_date = '';
																newRows[rowIndex].exp_date = '';
																newRows[rowIndex].mrp = '0.00';
																newRows[rowIndex].amount = '0';
																if (newRows[rowIndex].batch_no.length > 0) {
																	var batchList = [...batchOptions];
																	const isBatchExists = batchList.some(
																		(batch) => batch.key === newRows[rowIndex].batch_no,
																	);
																	if (!isBatchExists) {
																		batchList.push({
																			key: newRows[rowIndex].batch_no,
																			value: newRows[rowIndex].batch_no,
																		});

																		setBatchOptions(batchList);
																	}
																} else {
																	newRows[rowIndex].batch_no = '';
																}
																setRows(newRows);
																if (onChange) {
																	onChange(newRows);
																}
															} else if (value) {
																handleBatchChange(rowIndex, value, col?.name);
															}
														}}
														onSelect={(option) => {
															handleBatchChange(rowIndex, option.value, col?.name);
														}}
														options={batchOptions}
														returnInputValue={false}
														isLocalValidationError={
															errorCells.some(
																(cell) => cell.index === rowIndex && cell.name === col.name,
															)
																? true
																: false
														}
														onAddNew={() => {
															if (
																row?.product_id !== undefined &&
																row?.product_id !== null &&
																row?.product_id !== ''
															) {
																setIsOpenProductBatchModel(true);
																setTableIndex(rowIndex);
															} else {
																toast.error('Please first select product in current table');
															}
														}}
														onClickDropdownIcon={() => {
															if (row?.product_id !== undefined && row?.product_id !== null) {
																handleFetchBatchNumber(rowIndex, col?.name, row?.product_id);
															}
														}}
														onfucusCapture={(e) => {
															if (row?.product_id !== undefined && row?.product_id !== null) {
																handleFetchBatchNumber(rowIndex, col?.name, row?.product_id);
															}
														}}
													/>
												) : col.type === 'product_name' ? (
													<InputSelect
														placeholder="Select Product"
														value={row[col.name]}
														onChange={(value) => {
															handleProductInputChange(rowIndex, value);
														}}
														onSelect={(option) => handleProductInputChange(rowIndex, option.key)}
														isLocalValidationError={
															errorCells.some(
																(cell) => cell.index === rowIndex && cell.name === col.name,
															)
																? true
																: false
														}
														options={productOptions}
														disabled={!isDraft}
														returnInputValue={false}
														propInputRef={handleRef(rowIndex, cellIndex)}
													/>
												) : col.type === 'date' ? (
													<InputField
														type="text"
														name={col.name}
														value={row[col.name]}
														error={errorCells.some(
															(cell) => cell.index === rowIndex && cell.name === col.name,
														)}
														onChange={(value) => handleDateChange(rowIndex, value, col.name)}
														placeholder="MM/YY"
														maxLength="5"
														ref={handleRef(rowIndex, cellIndex)}
													/>
												) : (
													<InputField
														type={col.type}
														name={col.name}
														value={row[col.name]}
														onChange={(value) => {
															handleChangeDefault(rowIndex, value, col.name);
														}}
														disabled={col.name === 'quantity' ? getQuantityDisabled() : false}
														error={errorCells.some(
															(cell) => cell.index === rowIndex && cell.name === col.name,
														)}
														onKeyDown={(e) => handleKeyDown(rowIndex, e)}
														ref={handleRef(rowIndex, cellIndex)}
													/>
												)}
											</td>
										))}

										<td>
											{pbId !== undefined && rows.length === 1 ? (
												<></>
											) : (
												<button
													className="delete mt-0 bg-transparent"
													onClick={() => deleteRow(rowIndex)}
													tabIndex="-1"
												>
													<svg
														width="24"
														height="24"
														viewBox="0 0 24 24"
														fill="none"
													>
														<path
															d="M3 6H5H21"
															stroke="#FF0000"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
														<path
															d="M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6"
															stroke="#FF0000"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
														<path
															d="M10 11V17"
															stroke="#FF0000"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
														<path
															d="M14 11V17"
															stroke="#FF0000"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												</button>
											)}
										</td>
									</tr>
								))}
							</tbody>
						)}
					</table>
				</div>
				<span
					className="d-flex "
					style={{
						border: '1px solid #C3CBDC',
						height: '36px',
						backgroundColor: '#F6F6F9',
						display: 'flex',
						alignItems: 'center',
						paddingLeft: '10px',
						paddingRight: '100px',
						justifyContent: 'space-between',
						width: '100%',
						margin: 'auto',
					}}
				>
					<div>
						<span
							style={{
								color: 'var(--sub, #555B67)',
								fontWeight: '400',
								fontSize: '14px',
							}}
						>
							Total Items :
						</span>
						<span
							style={{
								color: 'var(--sub, #555B67)',
								fontWeight: '700',
								fontSize: '14px',
								marginLeft: '5px',
							}}
						>
							{numberOfProducts}
						</span>
					</div>
				</span>

				{(!disabled || (pbId && isDraft)) && (
					<span style={{ display: 'flex', justifyContent: 'right' }}>
						<button
							className="bg-transparent text-dark add-new-row-button mt-2"
							onClick={addRow}
						>
							Add New Row
							<i
								className="fa fa-plus"
								style={{
									marginLeft: '.5rem',
									strokeWidth: '.1rem',
									color: 'var(--brand-color)',
									WebkitTextStrokeColor: '#fff',
									WebkitTextStrokeWidth: '1px',
								}}
							/>
						</button>
					</span>
				)}
			</div>
			<div className="text-start">
				{showError && (
					<ErrorModal
						show={showError}
						isOpen={showError}
						onClose={CloseErrorModal}
						title="Error in Add Inventory Table"
						error={errorCells}
					/>
				)}
			</div>
			{isOpenProductBatchModel && (
				<AddProductBatchModel
					isOpenProductBatchModel={isOpenProductBatchModel}
					setIsOpenProductBatchModel={setIsOpenProductBatchModel}
					getProductBatchList={(productId, newBatchId) => {
						if (newBatchId) {
							setNewlyCreatedBatchIds((prev) => [...prev, newBatchId]);
						}
						handleFetchBatchNumber(tableIndex, 'batch_no', productId);
					}}
					tableIndex={tableIndex}
					handleBatchChange={handleBatchChange}
					invoiceTable={rows}
					setInvoiceTable={setRows}
				/>
			)}
		</>
	);
};

export default PurchaseBillsTable;
